import yup from '../utils/yup'
import moment from 'moment'
import { ContactFormValue } from './components/question-set-answering/base/contact-form/types'
import { Question, QuestionSetResponse, QuestionType, isTextBlockQuestionType, OrgNamedRef } from './types'
import { OTHER_OPTION } from './utils'

const REQUIRED_FIELD = 'Required field'
const PHONE_VALIDATION_TEXT = 'Phone number must be a valid US phone number'
const INTERNATIONAL_PHONE_VALIDATION_TEXT = 'Phone number must be a valid phone number'

const buildPhoneValidationSchema = (internationalPhoneEnabled: boolean) =>
  internationalPhoneEnabled
    ? yup.string().e164PhoneNumber(INTERNATIONAL_PHONE_VALIDATION_TEXT).nullable()
    : yup.string().phoneNumber(PHONE_VALIDATION_TEXT).nullable()

export const organizationSchema = yup.object<OrgNamedRef>({
  id: yup.number().required(),
  name: yup.string().required(),
  timeZone: yup.string(),
  website: yup.string(),
})

const answerWithCheckboxValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.array().ensure().of(yup.string()).min(1, 'Please choose at least one option').required(REQUIRED_FIELD),
  otherwise: yup.array().ensure().of(yup.string()).nullable().notRequired(),
})

const answerStringValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.string().trim().required(REQUIRED_FIELD),
  otherwise: yup.string().trim().nullable().notRequired(),
})

const answerNumberValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.number().min(0).required(REQUIRED_FIELD),
  otherwise: yup.number().nullable().notRequired(),
})

const answerTrueFalseValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.boolean().required(REQUIRED_FIELD),
  otherwise: yup.boolean().nullable().notRequired(),
})

const answerEmailValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.string().required(REQUIRED_FIELD).email('Invalid email'),
  otherwise: yup.string().nullable().notRequired().email('Invalid email'),
})

const buildAnswerPhoneValidationSchema = (internationalPhoneEnabled: boolean) => {
  const phoneValidationSchema = buildPhoneValidationSchema(internationalPhoneEnabled)
  return yup.mixed().when('isRequired', {
    is: true,
    then: phoneValidationSchema,
    otherwise: phoneValidationSchema.notRequired(),
  })
}

const buildPhonesSchema = (internationalPhoneEnabled: boolean) => {
  const phoneValidationSchema = buildPhoneValidationSchema(internationalPhoneEnabled)
  return yup.object({
    phoneNumber: phoneValidationSchema,
    homePhoneNumber: phoneValidationSchema,
    workPhoneNumber: phoneValidationSchema,
  })
}

const answerLocationValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup.object({
    location: yup.object({
      address: yup.object({
        line1: yup.string().required(REQUIRED_FIELD),
        line2: yup.string().notRequired().nullable(),
        city: yup.string().notRequired().nullable(),
        state: yup.string().required(REQUIRED_FIELD),
        zip: yup.string().notRequired().nullable(),
        country: yup.string().required(REQUIRED_FIELD),
      }),
    }),
  }),
  otherwise: yup
    .object({
      location: yup.object({
        address: yup.object({
          line1: yup.string().notRequired(),
          line2: yup.string().notRequired().nullable(),
          city: yup.string().notRequired(),
          state: yup.string().notRequired(),
          zip: yup.string().notRequired().nullable(),
          country: yup.string().notRequired(),
        }),
      }),
    })
    .notRequired(),
})

const buildAnswerContactValidationSchema = (internationalPhoneEnabled: boolean) =>
  yup.mixed<ContactFormValue>().when('isRequired', {
    is: true,
    then: yup
      .object({
        givenName: yup.string().required(REQUIRED_FIELD),
        familyName: yup.string().required(REQUIRED_FIELD),
        relationship: yup.string().required(REQUIRED_FIELD),
        email: yup.string().notRequired().nullable().email(),
      })
      .concat(
        // @ts-ignore
        buildPhonesSchema(internationalPhoneEnabled).atLeastOneOf(
          ['phoneNumber', 'homePhoneNumber', 'workPhoneNumber'],
          'Requires at least one phone number',
        ),
      ),
    otherwise:
      // @ts-ignore
      yup
        .object({
          givenName: yup.string().notRequired(),
          familyName: yup.string().notRequired(),
          relationship: yup.string().notRequired(),
          email: yup.string().notRequired().nullable().email(),
        })
        .concat(
          // @ts-ignore
          buildPhonesSchema(internationalPhoneEnabled),
        )
        .test('allOrNone', 'Requires first name, last name, relationship, any phone or none', function (value) {
          const requiredFields = ['givenName', 'familyName', 'relationship']
          const oneOfFields = ['phoneNumber', 'homePhoneNumber', 'workPhoneNumber']

          return (
            (requiredFields.every(key => value[key]) && oneOfFields.some(key => value[key])) ||
            [...requiredFields, ...oneOfFields].every(key => !value[key])
          )
        }),
  })

const answerDateOfBirthValidationSchema = yup.mixed().when('isRequired', {
  is: true,
  then: yup
    .date()
    .nullable()
    .required(REQUIRED_FIELD)
    .test('age', 'Must be at least 13 years old', value => moment().diff(moment(value), 'years') >= 13),
  otherwise: yup
    .date()
    .nullable()
    .notRequired()
    .test('age', 'Must be at least 13 years old', value => !value || moment().diff(moment(value), 'years') >= 13),
})

const answerSchema = yup.lazy(value => {
  if (Array.isArray(value)) {
    return answerWithCheckboxValidationSchema
  } else if (typeof value === 'number') {
    return answerNumberValidationSchema
  } else if (typeof value === 'boolean') {
    return answerTrueFalseValidationSchema
  }
  return answerStringValidationSchema
})

const otherAnswerValidationSchema = yup.mixed().when(['disabled', 'response'], {
  is: (disabled: boolean, response: any) => !disabled && Array.isArray(response) && response.includes(OTHER_OPTION),
  then: yup.string().trim().required("Required field when 'Other' is selected"),
  otherwise: yup.string().trim().nullable(true).notRequired(),
})

export const questionValidationSchema = yup.object().shape({
  disabled: yup.boolean().default(false),
  questionId: yup.number(),
  answerId: yup.number().notRequired(),
  question: yup.string(),
  options: yup.array(),
  questionType: yup.string(),
  isRequired: yup.boolean(),
  otherAnswer: otherAnswerValidationSchema,
})

const getSchemaByQuestionType = (questionType: string, internationalPhoneEnabled: boolean) => {
  switch (questionType as QuestionType) {
    case QuestionType.EMAIL:
      return answerEmailValidationSchema
    case QuestionType.PHONE_NUMBER:
      return buildAnswerPhoneValidationSchema(internationalPhoneEnabled)
    case QuestionType.EMERGENCY_CONTACT:
      return buildAnswerContactValidationSchema(internationalPhoneEnabled)
    case QuestionType.HOME_ADDRESS:
      return answerLocationValidationSchema
    case QuestionType.DATE_OF_BIRTH:
      return answerDateOfBirthValidationSchema
    case QuestionType.TEXT_BLOCK:
      return yup.mixed().nullable(true).notRequired()
    default:
      break
  }

  return answerSchema
}

const buildAnswerValidationSchema = (internationalPhoneEnabled: boolean) =>
  // @ts-ignore
  yup.lazy(({ disabled, questionType }) => {
    const skipResponseValidation = disabled || isTextBlockQuestionType(questionType)
    return yup
      .object()
      .shape({
        response: skipResponseValidation
          ? yup.mixed().nullable(true).notRequired()
          : getSchemaByQuestionType(questionType, internationalPhoneEnabled),
      })
      .concat(questionValidationSchema)
  })

export const getAnswerSetValidationSchema = (internationalPhoneEnabled = false) =>
  yup.object<QuestionSetResponse>().shape({
    answerSetId: yup.number().nullable().notRequired(),
    name: yup.string().nullable().notRequired(),
    // @ts-ignore
    organization: organizationSchema.optional(),
    questionSetId: yup.number().nullable().notRequired(),
    questions: yup.array<Question>().of(buildAnswerValidationSchema(internationalPhoneEnabled)),
  })

export const answerSetValidationSchema = getAnswerSetValidationSchema(false)
